import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Data } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppComponent } from 'app.component';
import { combineLatest, map, of, Subscription, switchMap } from 'rxjs';
import { waitForTranslationLoaded } from 'shared/util/wait-translations-loaded';
import { RouteHelperService } from './route-helper.service';

@Injectable({
    providedIn: 'root',
})
export class LayoutService {
    private layoutComponentRef!: AppComponent;
    private subscriptions: { [key: string]: Subscription } = {};

    constructor(
        private routeHelperService: RouteHelperService,
        private titleService: Title,
        private translate: TranslateService,
    ) {
        this.subscriptions.routeData = this.routeHelperService
            .getFinalRouteData()
            .pipe(
                switchMap((data) => combineLatest([of(data), waitForTranslationLoaded(translate)])),
                map(([data, translationLoaded]) => data),
            )
            .subscribe((data: Data) => {
                this.enableAllLayoutPanels();

                if (data && data['layout']) {
                    if (data['layout']['header'] === false) {
                        this.disableHeader();
                    }

                    if (data['layout']['sidebar'] === false) {
                        this.disableSidebar();
                    }
                }

                if (data['pageTitle']) {
                    const appTitle = this.titleService.getTitle();
                    let newAppTitle = '';
                    if (!Array.isArray(data['pageTitle'])) {
                        data['pageTitle'] = [data['pageTitle']];
                    }

                    data['pageTitle'].forEach((subTitle: any) => {
                        newAppTitle = newAppTitle.concat(this.translate.instant(subTitle) ?? subTitle, ' | ');
                    });
                    newAppTitle = newAppTitle.concat('Shiji');
                    this.titleService.setTitle(newAppTitle ?? appTitle);
                }
            });
    }

    registerLayoutComponent(component: AppComponent) {
        this.layoutComponentRef = component;
    }

    public disableUserMenu() {
        this.layoutComponentRef.manualLayoutOverrides.update((current) => ({ ...current, header: false }));
    }

    public disableNotificationsMenu() {
        this.layoutComponentRef.manualLayoutOverrides.update((current) => ({ ...current, header: false }));
    }

    public disableHeader() {
        this.layoutComponentRef.manualLayoutOverrides.update((current) => ({ ...current, header: false }));
    }

    public disableSidebar() {
        this.layoutComponentRef.manualLayoutOverrides.update((current) => ({ ...current, sidebar: false }));
    }

    private enableAllLayoutPanels() {
        // Setting the values to null instead of true will allow the manualLayoutOverrides signal to
        // no longer overwrite the layout's computed values, and only use the
        // navigationEnd and localStorageChange related signals to determine the layout.
        this.layoutComponentRef.manualLayoutOverrides.set({ header: null, sidebar: null });
    }
}
